import React, { useState, useEffect } from 'react';

import { AlertCard } from '../../../../components/AlertCard';
import { type UserAccessNotice } from '../../../../utils/userEntityAccess';
import { useSsoRestriction } from '../../contexts';
import { useBuyerLinking } from '../../hooks/useBuyerLinking';
import { SsoRestrictionTooltip } from '../SsoRestrictionTooltip';

import { LinkBuyerButton } from './LinkBuyerButton';
import { LinkBuyerModal } from './LinkBuyerModal';
import { SelectedBuyersList } from './SelectedBuyersList';
import { BuyerSelectOption } from './types';

interface BuyerLinkingFieldProps {
  userId: string;
  userName: string;
  initialLinkedBuyers?: BuyerSelectOption[];
  disabled?: boolean;
  /** Set when this user must not be linked to another buyer. */
  linkBlock?: UserAccessNotice | null;
  /** Off when a warning above already explains the block. */
  showLinkBlockMessage?: boolean;
  onLinkedBuyersChange?: (count: number) => void;
}

export const BuyerLinkingField: React.FC<BuyerLinkingFieldProps> = ({
  userId,
  userName,
  initialLinkedBuyers = [],
  disabled = false,
  linkBlock = null,
  showLinkBlockMessage = true,
  onLinkedBuyersChange,
}) => {
  const { isSsoUser } = useSsoRestriction();
  // Buyer links are managed by the identity provider for SSO users.
  const isDisabled = disabled || isSsoUser;
  const [linkedBuyers, setLinkedBuyers] =
    useState<BuyerSelectOption[]>(initialLinkedBuyers);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { unlinkUserFromBuyer, linkUserToBuyer } = useBuyerLinking();

  // Update local state when initial data changes
  useEffect(() => {
    setLinkedBuyers(initialLinkedBuyers);
  }, [initialLinkedBuyers]);

  useEffect(() => {
    onLinkedBuyersChange?.(linkedBuyers.length);
  }, [linkedBuyers.length, onLinkedBuyersChange]);

  const handleRemoveBuyer = async (buyerToRemove: BuyerSelectOption) => {
    try {
      await unlinkUserFromBuyer(buyerToRemove.value, userId);
      // Update local state to remove the buyer
      const updatedBuyers = linkedBuyers.filter(
        (buyer) => buyer.value !== buyerToRemove.value
      );
      setLinkedBuyers(updatedBuyers);
    } catch (error) {
      // Error is already handled in the hook
    }
  };

  const handleRoleChange = async (updatedBuyer: BuyerSelectOption) => {
    try {
      // Update the role by calling the BuyerAddUser mutation with the new role
      await linkUserToBuyer(updatedBuyer, userId);

      // Update local state immediately for UI feedback
      const updatedBuyers = linkedBuyers.map((buyer) =>
        buyer.value === updatedBuyer.value ? updatedBuyer : buyer
      );
      setLinkedBuyers(updatedBuyers);
    } catch (error) {
      // Error is already handled in the hook
    }
  };

  const handleModalSuccess = (newLinkedBuyer: BuyerSelectOption) => {
    // Add the newly linked buyer to the local state immediately
    // Check for duplicates to avoid adding the same buyer twice
    setLinkedBuyers((prev) => {
      const exists = prev.some((buyer) => buyer.value === newLinkedBuyer.value);
      if (exists) {
        return prev; // Don't add if already exists
      }
      return [...prev, newLinkedBuyer];
    });
    setIsModalOpen(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium text-foreground">
              Linked Buyers
            </h3>
            <SsoRestrictionTooltip />
            {linkedBuyers.length > 0 && (
              <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-primary-700 bg-primary-100 rounded-full">
                {linkedBuyers.length}
              </span>
            )}
          </div>
          <p className="text-sm text-default-500 mt-1">
            Link this user to buyers to give them access to buyer-specific
            features and chats.
          </p>
        </div>
        <LinkBuyerButton
          onPress={() => setIsModalOpen(true)}
          disabled={isDisabled || !!linkBlock}
        />
      </div>

      {linkBlock && showLinkBlockMessage && (
        <AlertCard
          type="warning"
          title={linkBlock.title}
          message={linkBlock.message}
        />
      )}

      {linkedBuyers.length > 0 ? (
        <SelectedBuyersList
          buyers={linkedBuyers}
          onDeleteBuyer={handleRemoveBuyer}
          onBuyerUserRoleChange={handleRoleChange}
          userName={userName}
          userId={userId}
          disabled={isDisabled}
        />
      ) : (
        <div className="text-center py-6 bg-default-50 rounded-lg border-2 border-dashed border-default-200">
          <p className="text-default-500 mb-2">
            This user is not linked to any buyers yet.
          </p>
          <p className="text-sm text-default-400">
            Click "Link Buyer" to search and link this user to buyers.
          </p>
        </div>
      )}

      <LinkBuyerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        userId={userId}
        userName={userName}
        linkedBuyers={linkedBuyers}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};
